// src/components/common/Button.tsx
import React from 'react';
import LoadingSpinner from './LoadingSpinner';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  variant?: 'primary' | 'secondary' | 'info' | 'outline' | 'danger' | 'ghost' | 'glass';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  allowWrap?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  isLoading,
  variant = 'primary',
  size = 'md',
  icon,
  className = '',
  disabled,
  allowWrap = false,
  ...props
}) => {
  // overflow-hidden: nowrap labels must not paint over sibling flex/grid cells when min-w-0 shrinks the button.
  const baseStyles = `font-bold rounded-xl transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-200 flex flex-row items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-bg active:scale-95 max-w-full min-w-0 overflow-hidden ${allowWrap ? 'whitespace-normal text-center leading-snug h-auto' : 'whitespace-nowrap'}`;

  const stateStyles = disabled || isLoading
    ? "opacity-50 cursor-not-allowed transform-none pointer-events-none grayscale-[0.5] shadow-none" 
    : "hover:-translate-y-0.5 active:scale-95 cursor-pointer";

  // ZMIANA: Wzmocnione cienie i efekty glow dla wszystkich wariantów
  const variants = {
    primary: "bg-primary hover:bg-primary-hover text-white shadow-lg shadow-green-900/20 border border-transparent",
    secondary: "bg-gray-700 hover:bg-gray-600 text-white shadow-lg shadow-black/20 border border-gray-600",
    // Info: Używany do zapisu (Blue)
    info: "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/30 border border-blue-500/20",
    outline: "border-2 border-primary/50 text-primary-light hover:bg-primary/10 shadow-none",
    // Danger: Używany do usuwania
    danger: "bg-red-600/90 hover:bg-red-500 text-white shadow-lg shadow-red-900/30 border border-red-500/20",
    ghost: "bg-transparent text-gray-400 hover:text-white hover:bg-white/5 border border-transparent shadow-none",
    // Glass: Używany do 'Cancel'
    glass: "glass-card hover:bg-white/10 border-white/10 text-white shadow-lg shadow-black/10"
  };

  const sizes = {
    sm: "py-2 px-3 sm:px-4 text-sm min-h-[40px]",
    md: "py-3 px-4 sm:px-6 text-base min-h-[48px]",
    lg: "py-4 px-6 sm:px-8 text-lg min-h-[52px]"
  };

  return (
    <button
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={`
        ${baseStyles} 
        ${variants[variant]} 
        ${sizes[size]} 
        ${stateStyles} 
        ${className}
      `}
      {...props}
    >
      {isLoading ? (
        <LoadingSpinner size="sm" inline />
      ) : (
        icon && <span className="inline-flex items-center shrink-0">{icon}</span>
      )}
      <span className={`min-w-0 max-w-full ${allowWrap ? '' : 'truncate'}`}>{children}</span>
    </button>
  );
};